import React from 'react'
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
} from '@mui/material'
import {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  NotificationPreferences,
} from '@store/api/notificationApi'
import {
  useGetNotificationsQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
} from '@store/api/notificationApi'
import NotificationList from '@components/notifications/NotificationList'

const CHANNELS = [
  { key: 'emailNotifications', label: 'Email Notifications', description: 'Receive notifications via email' },
  { key: 'pushNotifications', label: 'Push Notifications', description: 'Receive browser push notifications' },
] as const

const TYPES = [
  { key: 'notifyComments', label: 'Comments', description: 'When someone comments on your posts or reviews' },
  { key: 'notifyFollows', label: 'Follows', description: 'When someone follows you' },
  { key: 'notifyLikes', label: 'Likes', description: 'When someone likes your content' },
  { key: 'notifyNewReviews', label: 'New Reviews', description: 'New reviews from people you follow' },
  { key: 'notifySystemAlerts', label: 'Security Alerts', description: 'Important account and security notifications' },
  { key: 'notifyAnnouncements', label: 'Announcements', description: 'New features and platform announcements' },
] as const

const NotificationSettings: React.FC = () => {
  const { data: prefs, isLoading: prefsLoading } = useGetNotificationPreferencesQuery()
  const [updatePreferences, { isLoading: updating, isSuccess, isError }] = useUpdateNotificationPreferencesMutation()

  const { data: notificationsData, isLoading: notificationsLoading } = useGetNotificationsQuery({ page: 1, limit: 50 })
  const [markAsRead] = useMarkAsReadMutation()
  const [markAllAsRead] = useMarkAllAsReadMutation()
  const [deleteNotification] = useDeleteNotificationMutation()

  const handleToggle = (key: keyof NotificationPreferences) => async () => {
    if (!prefs) return
    await updatePreferences({ [key]: !prefs[key] })
  }

  if (prefsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Notification Settings
      </Typography>

      {isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Preferences saved
        </Alert>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to save preferences. Please try again.
        </Alert>
      )}

      {/* Delivery channels */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Delivery Channels
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how you receive notifications
          </Typography>
          {CHANNELS.map(({ key, label, description }) => (
            <Box key={key}>
              <FormControlLabel
                control={
                  <Switch
                    checked={prefs?.[key] ?? true}
                    onChange={handleToggle(key)}
                    disabled={updating}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">{description}</Typography>
                  </Box>
                }
                sx={{ width: '100%', ml: 0, justifyContent: 'space-between', flexDirection: 'row-reverse', mb: 1 }}
              />
              <Divider />
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Notification types */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Notification Types
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which events trigger notifications
          </Typography>
          {TYPES.map(({ key, label, description }) => (
            <Box key={key}>
              <FormControlLabel
                control={
                  <Switch
                    checked={prefs?.[key] ?? true}
                    onChange={handleToggle(key)}
                    disabled={updating}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">{description}</Typography>
                  </Box>
                }
                sx={{ width: '100%', ml: 0, justifyContent: 'space-between', flexDirection: 'row-reverse', mb: 1 }}
              />
              <Divider />
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* All notifications */}
      <Card>
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            All Notifications
          </Typography>
        </CardContent>
        <NotificationList
          notifications={notificationsData?.notifications ?? []}
          isLoading={notificationsLoading}
          onMarkAsRead={(id) => markAsRead(id)}
          onMarkAllAsRead={() => markAllAsRead()}
          onDelete={(id) => deleteNotification(id)}
        />
      </Card>
    </Box>
  )
}

export default NotificationSettings
